import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { ChevronLeft, Film, Clapperboard, Camera, Video, Projector, Tv2, Check, Sparkles } from 'lucide-react';

interface TimeframeOption {
  id: string;
  name: string;
  description: string;
  years: string;
  examples: string;
  icon: React.ReactNode;
}

const timeframes: TimeframeOption[] = [
  {
    id: '1970-1989',
    name: '70s & 80s Classics',
    description: 'Golden age of blockbusters and genre-defining films',
    years: '1970-1989',
    examples: 'Star Wars, The Godfather, E.T.',
    icon: <Film className="h-6 w-6" />
  },
  {
    id: '1990-1999',
    name: '90s Gems',
    description: 'Era of groundbreaking special effects and indie breakthroughs',
    years: '1990-1999',
    examples: 'Pulp Fiction, The Matrix, Jurassic Park',
    icon: <Clapperboard className="h-6 w-6" />
  },
  {
    id: '2000-2009',
    name: '2000s Favorites',
    description: 'Digital revolution and franchise beginnings',
    years: '2000-2009',
    examples: 'The Dark Knight, Lord of the Rings, Avatar',
    icon: <Camera className="h-6 w-6" />
  },
  {
    id: '2010-2019',
    name: '2010s Hits',
    description: 'Superhero boom and streaming revolution',
    years: '2010-2019',
    examples: 'Inception, Avengers, Get Out',
    icon: <Video className="h-6 w-6" />
  },
  {
    id: '2020-present',
    name: '2020 till Present',
    description: 'Latest and greatest in cinema',
    years: '2020 till present',
    examples: 'Oppenheimer, Barbie, Dune',
    icon: <Projector className="h-6 w-6" />
  },
  {
    id: 'any',
    name: 'No Time Preference',
    description: 'Show me great movies from any era',
    years: 'All years',
    examples: 'Best movies regardless of release date',
    icon: <Tv2 className="h-6 w-6" />
  }
];

interface Props {
  selectedTimeframes: string[];
  onSelect: (timeframes: string[]) => void;
  onBack: () => void;
  showBackButton: boolean;
}

export function TimeframeSelection({ selectedTimeframes: initialSelectedTimeframes, onSelect, onBack, showBackButton }: Props) {
  const [selectedTimeframes, setSelectedTimeframes] = useState<string[]>(initialSelectedTimeframes);
  const [error, setError] = useState<string | null>(null);

  const handleTimeframeToggle = (timeframeId: string) => {
    setSelectedTimeframes(prev => {
      let newSelection: string[];
      
      if (timeframeId === 'any') {
        // If 'any' is selected, clear all other selections
        newSelection = prev.includes('any') ? [] : ['any'];
      } else {
        // If a specific timeframe is selected, remove 'any' if it's there
        newSelection = prev.filter(id => id !== 'any');
        
        if (prev.includes(timeframeId)) {
          newSelection = newSelection.filter(id => id !== timeframeId);
        } else {
          newSelection = [...newSelection, timeframeId];
        }
      }
      
      setError(null);
      return newSelection;
    });
  };

  const handleSubmit = () => {
    if (selectedTimeframes.length === 0) {
      // Default to 'any' if nothing is selected
      onSelect(['any']);
    } else {
      onSelect(selectedTimeframes);
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2 text-center">
        <h2 className="display text-2xl font-semibold sm:text-3xl">
          Pick your <span className="text-gradient-dusk">era</span>.
        </h2>
        <p className="text-muted-foreground">
          Select one or more time periods, or choose 'No Time Preference' for recommendations from any era.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {timeframes.map((timeframe) => {
          const isSelected = selectedTimeframes.includes(timeframe.id);
          return (
            <button
              key={timeframe.id}
              onClick={() => handleTimeframeToggle(timeframe.id)}
              className={cn('select-card p-5', isSelected && 'select-card-active')}
              aria-pressed={isSelected}
            >
              {isSelected && (
                <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
              )}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'shrink-0 rounded-2xl border border-white/[0.07] bg-white/[0.04] p-2.5 transition-colors duration-200',
                      isSelected ? 'text-primary' : 'text-muted-foreground'
                    )}
                  >
                    {timeframe.icon}
                  </div>
                  <div>
                    <h3 className={cn('font-semibold leading-tight', isSelected ? 'text-primary' : 'text-foreground')}>
                      {timeframe.name}
                    </h3>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                      {timeframe.years}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{timeframe.description}</p>
                <p className="text-xs italic tracking-wide text-muted-foreground/70">
                  e.g. {timeframe.examples}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <p className="text-center text-sm text-red-400">{error}</p>
      )}

      <div className="flex items-center gap-3">
        {showBackButton && (
          <button onClick={onBack} className="btn-ghost px-5 py-3">
            <ChevronLeft className="h-5 w-5" />
            Back
          </button>
        )}
        <button onClick={handleSubmit} className="btn-marquee flex-1 px-4 py-3.5 text-base">
          <Sparkles className="h-5 w-5" />
          Get Movie Recommendations
        </button>
      </div>
    </div>
  );
}